"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import { useHomeStore } from "@/store/useHomeStore";
import { useAuthSession } from "@/hooks/use-auth-session";
import { customerService } from "@/lib/api/services/customer";
import { Menu, ArrowRight, ChevronDown, X, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import clsx from "clsx";
import { useTranslation } from "../language/hooks/useTranslation";
import { cn } from "@/lib/utils";

type MegaGroup = {
  title: string;
  links: { label: string; href: string }[];
};

type MenuSection = {
  label: string;
  href: string;
  description: string;
  image: string;
  groups: MegaGroup[];
};

function NavAuthButtons({ mobile = false, onNavigate }: { mobile?: boolean; onNavigate?: () => void }) {
  
  const { data: session, isFetching: sessionLoading } = useAuthSession();
  const homeData = useHomeStore((state) => state.homeData);

  const { data: customer, isLoading: userLoading } = useQuery({
    queryKey: ["customer", "id"],
    queryFn: async () => {
      const response = await customerService.getCustomer();
      return response.customer;
    },
    initialData: homeData?.customer,
    staleTime: 0,
    enabled: session?.authenticated ?? false,
  });

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const isMarketingHomeHref = pathname === '/';

  const isPublicCheckout = pathname === "/public-esim/checkout";
  const slug = isPublicCheckout ? searchParams.get("slug") : null;
  const redirectTo = slug ? encodeURIComponent(`/esim/checkout?slug=${slug}`) : null;

  const loginHref = redirectTo ? `/login?redirectTo=${redirectTo}` : "/login";
  const registerHref = slug ? `/register?origin=esim&slug=${slug}` : "/register";
  const homeHref = "/home";

  if (mobile) {
    return (
      <>
        <Link
          href={loginHref}
          onClick={onNavigate}
          className="w-full px-5 py-4 text-sm font-semibold text-center text-brand-white bg-brand-surface hover:bg--brand-surface/70 rounded-full"
        >
          Login
        </Link>
        <Link
          href={registerHref}
          onClick={onNavigate}
          className="w-full px-5 py-4 text-sm font-semibold text-center text-brand-black bg-brand-grey rounded-full hover:bg--brand-grey/70"
        >
          Sign Up
        </Link>
      </>
    );
  }

  return (
    <>
      {!session?.authenticated ? (
          <>
            <Link
              href={loginHref}
              className={clsx(
                "lg:px-8 px-5 py-3 text-base 3xl:text-xl font-semibold text-brand-bg rounded-full transition-all duration-300 hover:bg-brand-grey/10 whitespace-nowrap",
                isMarketingHomeHref ? "text-brand-black" : "text-brand-white hover:text-brand-text hover:bg-brand-grey/10",
              )}
            >
              Log in
            </Link>
            <Link
              href={registerHref}
              className="sm:px-8 px-5 py-3 text-base 3xl:text-xl font-semibold bg-brand-grey text-brand-bg rounded-full hover:bg-brand-grey/70 text-black whitespace-nowrap">
              Sign Up
            </Link>
          </>
        ) : (
           <>
            <Link
              href={homeHref}
                className={clsx(
                "sm:px-8 px-5 py-3 text-base 3xl:text-xl font-semibold bg-brand-grey text-brand-black rounded-full hover:bg-brand-grey/70",
              )}
            >
              My Account
            </Link>
          </>
        )
      }
    </>
  )
}

// Pages that open on a full-bleed dark hero: the header overlays the hero instead
// of taking its own space above it. Their heroes leave top padding for it.
const OVERLAY_HEADER_PATHS = ["/personal-account", "/business-account", "/our-company"];

export default function Header() {
  const pathname = usePathname();
  const { t, tm } = useTranslation();

  const isMarketingHomeHref = pathname === '/';

  const [activeMenu, setActiveMenu] = useState<MenuSection | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMobileSection, setActiveMobileSection] = useState<MenuSection | null>(null);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [visible, setVisible] = useState(true);

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(title)) { next.delete(title); } else { next.add(title); }
      return next;
    });
  };
  const [scrolled, setScrolled] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const diff = currentY - lastScrollY.current;

      setScrolled(currentY > 20);

      if (currentY < 80) {
        setVisible(true);
      } else if (diff > 6) {
        setVisible(false);
        setActiveMenu(null);
      } else if (diff < -6) {
        setVisible(true);
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const logoSrc = isMarketingHomeHref ?  "/logo-dr.png" : "/logo-tr.png";

  const menu: MenuSection[] = [
    {
      label: t("navbar.personal.label"),
      href: "/personal-account",
      description: 'Discover Vell',
      image: "/shutterstock/personal-card.webp",
      groups: [
        {
          title: t("navbar.personal.groups.accounts.title"),
          links: [
            { label: t("navbar.personal.groups.accounts.links.openAccount"), href: "/register" },
          ],
        },
        {
          title: t("navbar.personal.groups.resources.title"),
          links: [
            {
              label: t("navbar.business.groups.resources.links.compare"),
              href: "/resources/international-money-transfer-comparison",
            },
            {
              label: t("navbar.business.groups.resources.links.rateTracker"),
              href: "/resources/rateTracker",
            },
            {
              label: t("navbar.business.groups.resources.links.swiftFinder"),
              href: "/resources/swift-code-finder",
            },
            {
              label: t("navbar.personal.groups.resources.links.security"),
              href: "/security-and-protection",
            },
            { 
              label: t("navbar.personal.groups.resources.links.contactUs"), 
              href: "/contact-us" 
            },
            {
              label: t("navbar.personal.groups.resources.links.faq"),
              href: "/frequently-asked-questions",
            },
          ],
        },
      ],
    },
    {
      label: t("navbar.business.label"),
      href: "/business-account",
      description: 'Discover Vell Business',
      image: "/shutterstock/nav-business.webp",
      groups: [
        {
          title: t("navbar.business.groups.accounts.title"),
          links: [
            {
              label: t("navbar.business.groups.accounts.links.openBusinessAccount"),
              href: "/register",
            },
          ],
        },
        {
          title: t("navbar.business.groups.resources.title"),
          links: [
            {
              label: t("navbar.business.groups.resources.links.compare"),
              href: "/resources/international-money-transfer-comparison",
            },
            {
              label: t("navbar.business.groups.resources.links.rateTracker"),
              href: "/resources/rateTracker",
            },
            {
              label: t("navbar.business.groups.resources.links.swiftFinder"),
              href: "/resources/swift-code-finder",
            },
            {
              label: t("navbar.business.groups.resources.links.security"),
              href: "/security-and-protection",
            },
            { 
              label: t("navbar.business.groups.resources.links.contactUs"), 
              href: "/contact-us" 
            },
            {
              label: t("navbar.business.groups.resources.links.faq"),
              href: "/frequently-asked-questions",
            },
          ],
        },
      ],
    },
    {
      label: "Travel",
      href: "/travel-esim",
      description: "Explore our eSIM Plans",
      image: "/shutterstock/traveller.webp",
      groups: [
        {
          title: "Our eSIM Services",
          links: [
            {
              label: 'eSIM',
              href: "/travel-esim",
            },
            {
              label: 'Compare eSIM Plans',
              href: "/travel-esim-comparison",
            },
            {
              label: 'Installation Guide',
              href: "/travel-esim/install-esim",
            },
            {
              label: 'Activation Guide',
              href: "/travel-esim/activate-esim",
            },
            {
              label: 'Terms and Conditions',
              href: "/travel-esim/terms-and-conditions",
            },
          ],
        },
        {
          title: "Resources",
          links: [
            { label: 'Contact Us', href: "/contact-us" },
            { label: 'FAQs', href: "/frequently-asked-questions" },
          ],
        }
      ],

    },
    {
      label: t("navbar.company.label"),
      href: "/our-company",
      description: 'Discover Our Company',
      image: "/shutterstock/company.webp",
      groups: [
        {
          title: t("navbar.company.groups.whoWeAre.title"),
          links: [
            {
              label: t("navbar.company.groups.whoWeAre.links.aboutUs"),
              href: "/about-vell",
            },
            {
              label: t("navbar.company.groups.whoWeAre.links.contactUs"),
              href: "/contact-us",
            },
          ],
        },
        {
          title: "General",
          links: [
            {
              label: "Blog",
              href: "/blog",
            },
            {
              label: "Legal",
              href: "/legal",
            }
          ]
        },
        {
          title: t("navbar.company.groups.joinUs.title"),
          links: [
            {
              label: t("navbar.company.groups.joinUs.links.careers"),
              href: "/career",
            },
          ],
        }
      ],
    },
  ];
  return (
    <>
    {activeMenu && (
      <div
        className="fixed inset-0 z-40 bg-black/60 transition-opacity duration-200"
        onMouseEnter={() => setActiveMenu(null)}
      />
    )}
    <header
      onMouseLeave={() => setActiveMenu(null)}
      className={clsx(
        "top-0 z-50 w-full py-3 sm:py-4 transition-all duration-300 ease-in-out px-4 sm:px-6 lg:px-8 text-brand-text",
        scrolled && isMarketingHomeHref
          ? "shadow-md !bg-brand-white-light"
          : "",
        isMarketingHomeHref
          ? "bg-transparent md:fixed"
          : OVERLAY_HEADER_PATHS.includes(pathname)
            ? "bg-transparent absolute"
            : "bg-brand-bg sticky",
        visible ? "translate-y-0" : "-translate-y-full",
      )}
    >
      <div className="lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-7 lg:gap-14 xl:gap-22">
            <Link href="/" className="flex items-center">
              <Image
                src={logoSrc}
                alt="Vell Logo"
                width={120}
                height={48}
                className="w-[90px] h-auto sm:w-[110px]"
                priority
                unoptimized
              />
            </Link>

            <nav className="items-center hidden gap-7 lg:gap-12 xl:gap-15 md:flex">
              {menu.map((section) => {
                const isActive =
                  activeMenu?.label === section.label || pathname.startsWith(section.href);
                return (
                  <div key={section.label} onMouseEnter={() => setActiveMenu(section)}>
                    <Link
                      href={section.href}
                      onClick={() => setActiveMenu(null)}
                      className={clsx(
                        "text-sm lg:text-base 3xl:text-xl font-medium transition-colors",
                        // One colour class at a time: a base colour alongside green would override it.
                        // Off the homepage links inherit the header's text colour.
                        isMarketingHomeHref
                          ? isActive
                            ? "text-brand-green"
                            : "hover:text-brand-green text-brand-black"
                          : isActive
                            ? "text-brand-green/85"
                            : "hover:text-brand-green",
                      )}
                    >
                      {section.label}
                    </Link>
                  </div>
                );
              })}
            </nav>
          </div>

          <div className="items-center hidden gap-3 md:flex">
            <Suspense fallback={null}>
              <NavAuthButtons />
            </Suspense>
          </div>
          <div className="flex items-center gap-1 md:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu 
                  className={cn("size-6 sm:size-7",
                    isMarketingHomeHref ? "text-brand-black" : "text-brand-white"
                  )
                  
                } />
              </Button>
            </SheetTrigger>

            <SheetContent
              side="right"
              className={clsx(
                "w-full !max-w-full !bg-brand-bg text-brand-text !border-0 flex flex-col [&>button]:hidden",
                isMarketingHomeHref ? "bg-brand-primary-black" : "bg-brand-bg",
              )}
              style={{ padding: "0" }}
            >
              <SheetTitle className="sr-only">Menu</SheetTitle>

              {/* Logo + close */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-transparent shrink-0">
                <Link href="/" onClick={() => setMobileMenuOpen(false)}>
                  <Image src='/logo-tr.png' alt="Vell Logo" width={80} height={32} className="w-20" priority />
                </Link>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-1 text-brand-text/70 hover:text-brand-text"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Section tabs */}
              <div className="flex gap-2 px-3 sm:px-6 py-4 border-b border-transparent shrink-0 max-sm:justify-start max-sm:overflow-x-scroll scrollbar">
                {menu.map((section) => {
                  const isActive = (activeMobileSection ?? menu[0]).label === section.label;
                  return (
                    <button
                      key={section.label}
                      onClick={() => { setActiveMobileSection(section); setOpenGroups(new Set()); }}
                      className={clsx(
                        "px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors",
                        isActive ? "bg-brand-surface text-brand-text" : "text-brand-text/50 hover:text-brand-text"
                      )}
                    >
                      {section.label}
                    </button>
                  );
                })}
              </div>

              {/* Discover link + collapsible groups */}
              {(() => {
                const activeSection = activeMobileSection ?? menu[0];
                return (
                  <>
                    <div className="px-6 py-5 border-b border-transparent shrink-0">
                      <Link
                        href={activeSection.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className="inline-flex items-center gap-1.5 text-base font-semibold group"
                      >
                        {activeSection.description}
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                      </Link>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                      {activeSection.groups.map((group) => {
                        const isOpen = openGroups.has(group.title);
                        return (
                          <div key={group.title} className="border-b border-transparent">
                            <button
                              onClick={() => toggleGroup(group.title)}
                              className="w-full flex items-center justify-between px-6 py-4 text-left"
                            >
                              <span className="text-sm font-medium">{group.title}</span>
                              <ChevronDown
                                className={clsx(
                                  "w-4 h-4 text-brand-text/50 transition-transform duration-200",
                                  isOpen && "rotate-180"
                                )}
                              />
                            </button>
                            {isOpen && (
                              <div className="px-6 pb-4 space-y-3">
                                {group.links.map((link) => (
                                  <Link
                                    key={link.href}
                                    href={link.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="block text-sm text-brand-text/60 hover:text-brand-text transition-colors"
                                  >
                                    {link.label}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}

              {/* Auth buttons pinned to bottom */}
              <div className="flex flex-col gap-3 px-6 py-6 border-t border-transparent shrink-0">
                <Suspense fallback={null}>
                  <NavAuthButtons mobile onNavigate={() => setMobileMenuOpen(false)} />
                </Suspense>
              </div>
            </SheetContent>
          </Sheet>
          </div>
        </div>
      </div>
      {activeMenu && (
        <div
          className={clsx(
            "absolute left-0 right-0 top-full shadow-2xl px-4 sm:px-6 lg:px-8 text-brand-text max-w-6xl 3xl:max-w-[1432px] mx-auto rounded-3xl",
            isMarketingHomeHref ? "bg-brand-surface border border-brand-stroke" : "bg-brand-card border border-brand-stroke",
          )}
        >
          <div className="max-w-6xl 3xl:max-w-[1432px] mx-auto py-8">
            <Link
              href={activeMenu.href}
              onClick={() => setActiveMenu(null)}
              className="inline-flex items-center gap-1 mb-12 text-xl group text-brand-text hover:text-brand-green"
            >
              {activeMenu.description}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </Link>

            <div className="flex gap-26">
              {activeMenu.groups.map((group) => (
                <div key={group.title}>
                  <h4 className="mb-3 text-sm 3xl:text-base font-semibold">{group.title}</h4>
                  <ul className="space-y-4 list-none pl-0">
                    {group.links.map((link) => (
                      <li key={link.href}>
                        <Link
                          href={link.href}
                          onClick={() => setActiveMenu(null)}
                          className="text-sm 3xl:text-base transition-colors text-brand-text/80 hover:text-brand-green"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
    </>
  );
}